import { Alert, Anchor, Group, Loader, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import classNames from "classnames";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router";
import type { ApiError } from "../../api";
import { useToast } from "../../mantine/notifications/useToast";
import type { components } from "../../types/api.types";
import { getErrorText } from "../../utils/get-error-text";

interface LoadOrErrorProps {
  loading?: boolean;
  loadingMessage?: string;
  loadingComponent?: ReactNode;
  error?: unknown;
  errorRedirectTo?: string;
  customErrors?: Record<number, string>;
  className?: string;
  children?: ReactNode;
}

export type UserFacingErrorWithButton =
  components["schemas"]["UserFacingErrorWithButton"];

export const ErrorButton = ({
  button_caption,
  button_url
}: UserFacingErrorWithButton) => {
  if (!(button_caption && button_url)) {
    return;
  }

  return (
    <Anchor
      component={Link}
      fw="inherit"
      inherit
      to={button_url}
      underline="never"
    >
      {button_caption}
    </Anchor>
  );
};

function LoadOrError({
  loading,
  loadingMessage,
  loadingComponent,
  error,
  errorRedirectTo,
  customErrors,
  className,
  children
}: LoadOrErrorProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (error && errorRedirectTo) {
      const errorText = getErrorText(error, customErrors);
      showToast({
        type: "error",
        message: errorText,
        withCloseButton: true,
        onClose: () => navigate(errorRedirectTo, { replace: true })
      });
    }
  }, [error, errorRedirectTo, customErrors, navigate, showToast]);

  if (loading) {
    if (loadingComponent) {
      return (
        <div className={classNames("LoadOrError", className)}>
          {loadingComponent}
        </div>
      );
    }

    return (
      <Stack
        align="center"
        className={classNames("LoadOrError", className)}
        gap={0}
      >
        <Loader />
        {loadingMessage && <Text mt="lg">{loadingMessage}</Text>}
      </Stack>
    );
  } else if (error) {
    const apiError = error as ApiError<UserFacingErrorWithButton>;
    const errorWithButton =
      apiError?.response && apiError.response.data?.button_caption
        ? apiError.response.data
        : null;
    return (
      <Alert
        className={classNames("LoadOrError", className)}
        color={errorWithButton ? "warning" : "error"}
        icon={errorWithButton ? <IconAlertTriangle /> : undefined}
      >
        <Group gap="md">
          {getErrorText(error)}
          {errorWithButton && <ErrorButton {...errorWithButton} />}
        </Group>
      </Alert>
    );
  }

  return <>{children}</>;
}

export { LoadOrError };
